import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";

import { Plan } from "../types/entities";

/**
 * Confirmation dialog for scheduling a cancellation (#2796), per the #2785
 * UX spec, flow (d). Cancellation is never a single click — this is the
 * required explicit confirmation step between `PricingTab`'s de-emphasized
 * "Cancel subscription" entry point and the actual
 * `POST /subscriptions/cancel` call (#2786).
 *
 * There is deliberately no "undo cancellation" action anywhere in this
 * component or its caller: per the confirmed #2785 UX answers
 * (https://gitlab.com/hipsquare/pimo/-/work_items/2785#note_3976958451),
 * self-service un-canceling is out of scope for V1 — an Owner who changes
 * their mind during the scheduled period must contact support, same as a
 * blocked downgrade.
 */
export interface CancelSubscriptionDialogState {
  isOpen: boolean;
  currentPlan?: Plan | null;
  /** The Stripe-confirmed date the subscription will actually end
   * (`subscription.cancelAt`), already formatted for display by the
   * caller — this component never formats dates itself. */
  periodEndDate?: string | null;
  isSubmitting?: boolean;
  errorMessage?: string;
  language: SupportedLanguage;
}

export type CancelSubscriptionDialogEvent =
  "close-cancel-dialog" | "confirm-cancel-subscription";

export const CancelSubscriptionDialog: PimoReactComponent<
  CancelSubscriptionDialogState,
  CancelSubscriptionDialogEvent,
  unknown
> = ({
  fireEvent,
  isOpen,
  currentPlan,
  periodEndDate,
  isSubmitting,
  errorMessage,
  language,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={isOpen}
      onClose={() => fireEvent?.("close-cancel-dialog")}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {getLocalizedMessage(language, "cancelSubscriptionDialog.title")}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {periodEndDate
            ? getLocalizedMessage(
                language,
                "cancelSubscriptionDialog.bodyWithDate",
                { periodEndDate, plan: currentPlan?.name ?? "" }
              )
            : getLocalizedMessage(
                language,
                "cancelSubscriptionDialog.bodyNoDate"
              )}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary }}
        >
          {getLocalizedMessage(language, "cancelSubscriptionDialog.keepUsing")}
        </Typography>
        {errorMessage ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={() => fireEvent?.("close-cancel-dialog")}
          disabled={!!isSubmitting}
          sx={{ textTransform: "none" }}
        >
          {getLocalizedMessage(language, "cancelSubscriptionDialog.keepPlan")}
        </Button>
        <Button
          onClick={() => fireEvent?.("confirm-cancel-subscription")}
          disabled={!!isSubmitting}
          variant="contained"
          color="warning"
          sx={{
            textTransform: "none",
            boxShadow: "none",
            color: theme.palette.warning.contrastText,
          }}
        >
          {isSubmitting
            ? "Submitting…"
            : getLocalizedMessage(
                language,
                "cancelSubscriptionDialog.scheduleCancellation"
              )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
